import React, { createContext, useContext, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import {
  useUserNotifications,
  useUnreadNotificationCount,
  useMarkNotificationAsRead,
  useDeleteNotification,
  useMarkAllNotificationsAsRead,
} from '@/hooks/useNotifications';
import { Notification as DbNotification } from '@/services/notificationService';
import { useApprovalMode } from './ApprovalModeContext';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'assignment' | 'status_change' | 'risk_issue' | 'approval' | 'deadline';
  isRead: boolean;
  createdAt: Date;
  entityId?: string;
  entityType?: 'activity' | 'deliverable' | 'material' | 'risk' | 'issue';
  url?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  addNotification: (notification: Omit<Notification, 'id' | 'isRead' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  markAllAsRead: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use TanStack Query hooks
  const { data: notificationsData, isLoading: isLoadingNotifications } = useUserNotifications();
  const { data: unreadCountData } = useUnreadNotificationCount();
  const markAsReadMutation = useMarkNotificationAsRead();
  const deleteNotificationMutation = useDeleteNotification();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  // Transform notifications from DB format to UI format
  const deriveNotificationType = (row: DbNotification): Notification['type'] => {
    if (row.issue_id || row.entity_type === 'risk' || row.entity_type === 'issue') return 'risk_issue';
    if (row.entity_type === 'activity' || row.entity_type === 'deliverable' || row.activity_id || row.deliverable_id) return 'status_change';
    return 'status_change';
  };

  const notifications: Notification[] = (notificationsData || []).map(n => ({
    id: n.id,
    title: n.title,
    message: n.body,
    type: deriveNotificationType(n),
    isRead: n.is_read,
    createdAt: new Date(n.created_at),
    entityId: n.issue_id || n.entity_id || n.activity_id || n.deliverable_id,
    entityType: (n.entity_type || (n.issue_id ? 'issue' : n.activity_id ? 'activity' : n.deliverable_id ? 'deliverable' : undefined)) as Notification['entityType'],
    url: n.url || undefined,
  }));

  const unreadCount = unreadCountData || 0;

  const addNotification = useCallback((notificationData: Omit<Notification, 'id' | 'isRead' | 'createdAt'>) => {
    // Show toast for new notification
    toast({
      title: notificationData.title,
      description: notificationData.message,
      duration: 5000,
    });
    // Note: In a real implementation, you'd also create the notification in the DB
    // For now, just showing the toast
  }, []);

  const markAsRead = useCallback((id: string) => {
    markAsReadMutation.mutate(id);
  }, [markAsReadMutation]);

  const deleteNotification = useCallback((id: string) => {
    deleteNotificationMutation.mutate(id);
  }, [deleteNotificationMutation]);

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      isLoading: isLoadingNotifications,
      addNotification,
      markAsRead,
      deleteNotification,
      markAllAsRead
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
